import { createContext, useContext, useState, ReactNode } from "react";

type Language = "pt" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  pt: {
    // Navbar
    "nav.search": "Buscar",
    "nav.properties": "Imóveis",
    "nav.contact": "Contato",
    "nav.schedule": "Agendar Visita",

    // Hero
    "hero.badge": "IMÓVEIS PREMIUM NO RIO DE JANEIRO",
    "hero.title": "Apartamentos, Coberturas e Casas na Barra da Tijuca e Zona Sul",
    "hero.subtitle": "Encontre o imóvel dos seus sonhos nas melhores localizações do Rio de Janeiro. Atendimento exclusivo e personalizado para você.",
    "hero.cta": "Fale com um Consultor",
    "hero.cta2": "Ver Imóveis Disponíveis",
    "hero.whatsapp": "Olá! Gostaria de mais informações sobre imóveis premium no Rio de Janeiro.",

    // Search
    "search.title": "Encontre Seu Imóvel Ideal",
    "search.neighborhood": "Bairro",
    "search.type": "Tipo",
    "search.bedrooms": "Quartos",
    "search.garage": "Vagas",
    "search.areaMin": "Área mín. (m²)",
    "search.priceMax": "Preço máx.",
    "search.button": "Buscar Imóveis",
    "search.bedroomsPlus": "quartos",
    "search.garagePlus": "vagas",

    // Property types
    "type.apartamento": "Apartamento",
    "type.cobertura": "Cobertura",
    "type.casa": "Casa",

    // Features
    "features.title": "Por Que Escolher a Topclass?",
    "features.subtitle": "Há mais de uma década conectando pessoas ao imóvel dos sonhos nas melhores regiões do Rio de Janeiro.",
    "features.verified.title": "Imóveis Verificados",
    "features.verified.desc": "Todos os imóveis são verificados pessoalmente pela nossa equipe antes de serem anunciados.",
    "features.locations.title": "Melhores Localizações",
    "features.locations.desc": "Especializados na Barra da Tijuca, Zona Sul e Tijuca — as regiões mais valorizadas do Rio.",
    "features.premium.title": "Atendimento Premium",
    "features.premium.desc": "Consultores exclusivos dedicados a encontrar o imóvel perfeito para o seu perfil.",
    "features.support.title": "Suporte Completo",
    "features.support.desc": "Acompanhamento jurídico e financeiro em todas as etapas da negociação.",

    // Neighborhoods
    "neighborhoods.title": "Regiões Que Atendemos",
    "neighborhoods.subtitle": "Especializados nas áreas mais nobres e valorizadas do Rio de Janeiro",
    "neighborhoods.viewProperties": "Ver imóveis →",
    "neighborhoods.barra": "A região mais valorizada da Zona Oeste com praias, shoppings e condomínios de alto padrão.",
    "neighborhoods.copacabana": "Um dos bairros mais icônicos do mundo, com orla deslumbrante e vida cultural vibrante.",
    "neighborhoods.ipanema": "Charme, sofisticação e uma das praias mais famosas do planeta na Zona Sul.",
    "neighborhoods.leblon": "O metro quadrado mais valorizado do Rio. Exclusividade e qualidade de vida incomparável.",
    "neighborhoods.botafogo": "Vista para o Pão de Açúcar, excelente infraestrutura e fácil acesso a toda a cidade.",
    "neighborhoods.tijuca": "Tradição e qualidade de vida com fácil acesso a todas as regiões do Rio.",
    "neighborhoods.recreio": "Praias paradisíacas, natureza exuberante e condomínios de alto padrão com tranquilidade.",

    // Contact
    "contact.title": "Encontre Seu Imóvel Ideal",
    "contact.subtitle": "Preencha o formulário ou fale diretamente com um de nossos consultores pelo WhatsApp. Estamos prontos para ajudá-lo a encontrar o imóvel perfeito.",
    "contact.whatsappBtn": "Falar pelo WhatsApp Agora",
    "contact.formTitle": "Solicite Informações",
    "contact.name": "Seu nome completo",
    "contact.email": "Seu e-mail",
    "contact.phone": "Seu telefone com DDD",
    "contact.interest": "Interesse em...",
    "contact.allTypes": "Todos os tipos",
    "contact.message": "Descreva o imóvel que você procura (bairro, tamanho, quartos, etc.)",
    "contact.submit": "Enviar Solicitação",
    "contact.toast.title": "Formulário enviado!",
    "contact.toast.desc": "Você será redirecionado para completar o envio. Entraremos em contato em breve!",
    "contact.whatsapp": "Olá! Gostaria de receber informações sobre imóveis premium no Rio de Janeiro.",

    // Listings
    "listings.title": "Imóveis à Venda",
    "listings.count": "imóveis encontrados nas melhores localizações do Rio",
    "listings.empty": "Nenhum imóvel encontrado com os filtros selecionados. Tente ajustar sua busca ou entre em contato conosco.",
    "listings.featured": "Destaque",
    "listings.whatsapp": "Falar pelo WhatsApp",
    "listings.whatsappMsg": "Olá! Tenho interesse no imóvel: {title} - {price}. Gostaria de mais informações.",
    "listings.suites": "suítes",
    "listings.code": "Cód",
    "listings.notFound": "Imóvel não encontrado",
    "listings.backToListings": "Voltar aos imóveis",

    // Neighborhood Page
    "neighborhood.premiumRegion": "Região Premium",
    "neighborhood.backHome": "Voltar ao início",
    "neighborhood.filterTitle": "Filtrar Imóveis em",
    "neighborhood.bedroomsPlus": "quartos",
    "neighborhood.spotsPlus": "vagas",
    "neighborhood.areaMin": "Área mín. (m²)",
    "neighborhood.priceMax": "Preço máx.",
    "neighborhood.searchButton": "Buscar Imóveis",
    "neighborhood.highlightsIn": "Destaques em",
    "neighborhood.exclusiveOpportunity": "Oportunidade Exclusiva",

    // Listing Detail
    "detail.featured": "Destaque",
    "detail.furnished": "Mobiliado",
    "detail.nearMetro": "Próximo ao Metrô",
    "detail.scheduleWhatsApp": "Tire suas dúvidas pelo Whatsapp",
    "detail.requestInfo": "Quero agendar uma visita",
    "detail.roomsTitle": "Cômodos e Espaços",
    "detail.bedrooms": "Quartos",
    "detail.including": "Incluindo",
    "detail.suite": "suíte",
    "detail.suites": "suítes",
    "detail.dormitories": "Dormitórios",
    "detail.bathroom": "Banheiro",
    "detail.bathrooms": "Banheiros",
    "detail.socialSuite": "Social e suíte",
    "detail.spot": "Vaga",
    "detail.spots": "Vagas",
    "detail.garage": "Garagem",
    "detail.totalArea": "Área total",
    "detail.floor": "Andar",
    "detail.propertyType": "Tipo do imóvel",
    "detail.salePrice": "Valor de Venda",
    "detail.code": "Código",
    "detail.condo": "Condomínio",
    "detail.month": "mês",
    "detail.year": "ano",
    "detail.whatsappChat": "Falar no WhatsApp",
    "detail.moreInfo": "Quero conhecer este imóvel",
    "detail.gallery": "Galeria de Fotos",
    "detail.video": "Vídeo do Imóvel",
    "detail.about": "Sobre o Imóvel",
    "detail.highlights": "Diferenciais do Imóvel",
    "detail.highlightsSub": "Características que tornam este imóvel único em {neighborhood}",
    "detail.fullDetails": "Detalhes Completos",
    "detail.location": "Localização Privilegiada",
    "detail.brokerTitle": "Corretor Responsável",
    "detail.scheduleTitle": "Agende Sua Visita Agora",
    "detail.scheduleSub": "Preencha seus dados e entraremos em contato em minutos. Sem compromisso!",
    "detail.fullName": "Nome completo*",
    "detail.namePlaceholder": "Seu nome",
    "detail.phonePlaceholder": "(21) 99999-9999",
    "detail.phoneLabel": "Telefone / WhatsApp*",
    "detail.emailLabel": "E-mail",
    "detail.messageLabel": "Mensagem",
    "detail.messagePlaceholder": "Gostaria de agendar uma visita...",
    "detail.submitSchedule": "Quero Agendar Minha Visita",
    "detail.dataPrivacy": "Seus dados estão seguros. Não compartilhamos com terceiros.",
    "detail.messageSent": "Mensagem Enviada!",
    "detail.contactSoon": "Entraremos em contato em breve.",
    "detail.orWhatsApp": "Ou fale agora pelo WhatsApp",
    "detail.notFound": "Não encontrou seu {type} ideal?",
    "detail.notFoundSub": "Conte-nos o que procura e nossa equipe encontrará as melhores opções para você.",
    "detail.requestReceived": "Recebemos sua solicitação!",
    "detail.personalizedOptions": "Entraremos em contato com opções personalizadas.",
    "detail.helpFind": "Quero ajuda para encontrar meu {type}",
    "detail.yourName": "Seu nome *",
    "detail.whatsappRequired": "WhatsApp *",
    "detail.emailOptional": "E-mail (opcional)",
    "detail.similarIn": "{type}s semelhantes em {neighborhood}",
    "detail.seeOther": "Veja outras opções que podem interessar você",
    "detail.whatsappMsg": "Olá! Tenho interesse no imóvel: {title} - {price}. Gostaria de agendar uma visita.",
    "detail.leadMsg": "Olá! Meu nome é {name}.\nTelefone: {phone}\nE-mail: {email}\nMensagem: {message}\n\nTenho interesse em: {title} - {price}",
    "detail.leadNotFound": "Olá! Meu nome é {name}.\nTelefone: {phone}\nE-mail: {email}\n\nNão encontrei meu {type} ideal, podem me ajudar?",
    "detail.notProvided": "não informado",

    // Launches
    "nav.launches": "Lançamentos",
    "launches.title": "Lançamentos Imobiliários",
    "launches.subtitle": "Conheça os melhores empreendimentos novos no Rio de Janeiro. Condições exclusivas de lançamento.",
    "launches.empty": "Nenhum lançamento disponível no momento. Volte em breve!",
    "launches.from": "A partir de",
    "launches.upTo": "Até",
    "launches.area": "Área",
    "launches.downPayment": "Entrada",
    "launches.delivery": "Entrega",
    "launches.typologies": "Tipologias",
    "launches.description": "Descrição",
    "launches.profitability": "Estudo de Rentabilidade",
    "launches.ctaTitle": "Receba Plantas e Book do Projeto",
    "launches.ctaDesc": "Fale com um consultor e receba todo o material do empreendimento pelo WhatsApp.",
    "launches.ctaButton": "Receber Material pelo WhatsApp",
    "launches.backToList": "Voltar aos Lançamentos",
    "launches.notFound": "Lançamento não encontrado.",
    "launches.form.title": "Simulação de Financiamento",
    "launches.form.subtitle": "Preencha seus dados e receba uma simulação personalizada.",
    "launches.form.name": "Nome completo",
    "launches.form.phone": "Telefone com DDD",
    "launches.form.email": "E-mail (opcional)",
    "launches.form.income": "Renda familiar mensal (R$)",
    "launches.form.downPayment": "Valor de entrada (R$)",
    "launches.form.submit": "Solicitar Simulação",
    "launches.form.sending": "Enviando...",
    "launches.form.success": "Solicitação enviada!",
    "launches.form.successDesc": "Entraremos em contato em breve com sua simulação.",
    "launches.form.error": "Erro ao enviar. Tente novamente.",
    "launches.carousel.title": "Lançamentos em Destaque",
    "launches.carousel.subtitle": "Os melhores empreendimentos novos com condições exclusivas",
    "launches.carousel.viewAll": "Ver Todos os Lançamentos",

    // Footer
    "footer.tagline": "Especialistas em imóveis de alto padrão na Barra da Tijuca, Zona Sul e Tijuca.",
    "footer.regions": "Regiões",
    "footer.contact": "Contato",
    "footer.rights": "Topclass Imobiliária Premium. Todos os direitos reservados. CRECI-RJ",
  },
  en: {
    // Navbar
    "nav.search": "Search",
    "nav.properties": "Properties",
    "nav.contact": "Contact",
    "nav.schedule": "Schedule Visit",

    // Hero
    "hero.badge": "PREMIUM REAL ESTATE IN RIO DE JANEIRO",
    "hero.title": "Apartments, Penthouses & Houses in Barra da Tijuca and South Zone",
    "hero.subtitle": "Find your dream property in the best locations in Rio de Janeiro. Exclusive and personalized service for you.",
    "hero.cta": "Talk to a Consultant",
    "hero.cta2": "View Available Properties",
    "hero.whatsapp": "Hi! I'd like more information about premium properties in Rio de Janeiro.",

    // Search
    "search.title": "Find Your Ideal Property",
    "search.neighborhood": "Neighborhood",
    "search.type": "Type",
    "search.bedrooms": "Bedrooms",
    "search.garage": "Parking",
    "search.areaMin": "Min area (m²)",
    "search.priceMax": "Max price",
    "search.button": "Search Properties",
    "search.bedroomsPlus": "bedrooms",
    "search.garagePlus": "spots",

    // Property types
    "type.apartamento": "Apartment",
    "type.cobertura": "Penthouse",
    "type.casa": "House",

    // Features
    "features.title": "Why Choose Topclass?",
    "features.subtitle": "Over a decade connecting people to their dream property in the finest areas of Rio de Janeiro.",
    "features.verified.title": "Verified Properties",
    "features.verified.desc": "All properties are personally verified by our team before being listed.",
    "features.locations.title": "Best Locations",
    "features.locations.desc": "Specialized in Barra da Tijuca, South Zone and Tijuca — Rio's most valued areas.",
    "features.premium.title": "Premium Service",
    "features.premium.desc": "Dedicated consultants committed to finding the perfect property for your profile.",
    "features.support.title": "Full Support",
    "features.support.desc": "Legal and financial guidance at every stage of the negotiation.",

    // Neighborhoods
    "neighborhoods.title": "Areas We Serve",
    "neighborhoods.subtitle": "Specialized in the finest and most valued areas of Rio de Janeiro",
    "neighborhoods.viewProperties": "View properties →",
    "neighborhoods.barra": "The most valued area of the West Zone with beaches, malls and luxury condominiums.",
    "neighborhoods.copacabana": "One of the most iconic neighborhoods in the world, with a stunning waterfront and vibrant culture.",
    "neighborhoods.ipanema": "Charm, sophistication and one of the most famous beaches on the planet in the South Zone.",
    "neighborhoods.leblon": "The highest-priced neighborhood in Rio. Unmatched exclusivity and quality of life.",
    "neighborhoods.botafogo": "Views of Sugarloaf Mountain, excellent infrastructure and easy access to the entire city.",
    "neighborhoods.tijuca": "Tradition and quality of life with easy access to all areas of Rio.",
    "neighborhoods.recreio": "Pristine beaches, lush nature and luxury condominiums with tranquility.",

    // Contact
    "contact.title": "Find Your Ideal Property",
    "contact.subtitle": "Fill out the form or talk directly to one of our consultants via WhatsApp. We're ready to help you find the perfect property.",
    "contact.whatsappBtn": "Chat on WhatsApp Now",
    "contact.formTitle": "Request Information",
    "contact.name": "Your full name",
    "contact.email": "Your e-mail",
    "contact.phone": "Your phone number",
    "contact.interest": "Interested in...",
    "contact.allTypes": "All types",
    "contact.message": "Describe the property you're looking for (neighborhood, size, bedrooms, etc.)",
    "contact.submit": "Submit Request",
    "contact.toast.title": "Form submitted!",
    "contact.toast.desc": "You'll be redirected to complete the submission. We'll get in touch soon!",
    "contact.whatsapp": "Hi! I'd like to receive information about premium properties in Rio de Janeiro.",

    // Listings
    "listings.title": "Properties for Sale",
    "listings.count": "properties found in the best locations in Rio",
    "listings.empty": "No properties found with the selected filters. Try adjusting your search or contact us.",
    "listings.featured": "Featured",
    "listings.whatsapp": "Chat on WhatsApp",
    "listings.whatsappMsg": "Hi! I'm interested in the property: {title} - {price}. I'd like more information.",
    "listings.code": "Code",
    "listings.notFound": "Property not found",

    // Neighborhood Page
    "neighborhood.premiumRegion": "Premium Region",
    "neighborhood.backHome": "Back to home",
    "neighborhood.filterTitle": "Filter Properties in",
    "neighborhood.bedroomsPlus": "bedrooms",
    "neighborhood.spotsPlus": "spots",
    "neighborhood.areaMin": "Min area (m²)",
    "neighborhood.priceMax": "Max price",
    "neighborhood.searchButton": "Search Properties",
    "neighborhood.highlightsIn": "Highlights in",
    "neighborhood.exclusiveOpportunity": "Exclusive Opportunity",
    "listings.backToListings": "Back to properties",

    // Listing Detail
    "detail.featured": "Featured",
    "detail.furnished": "Furnished",
    "detail.nearMetro": "Near Metro",
    "detail.scheduleWhatsApp": "Ask your questions on WhatsApp",
    "detail.requestInfo": "Schedule a visit",
    "detail.roomsTitle": "Rooms & Spaces",
    "detail.bedrooms": "Bedrooms",
    "detail.including": "Including",
    "detail.suite": "suite",
    "detail.suites": "suites",
    "detail.dormitories": "Bedrooms",
    "detail.bathroom": "Bathroom",
    "detail.bathrooms": "Bathrooms",
    "detail.socialSuite": "Social & suite",
    "detail.spot": "Spot",
    "detail.spots": "Spots",
    "detail.garage": "Garage",
    "detail.totalArea": "Total area",
    "detail.floor": "Floor",
    "detail.propertyType": "Property type",
    "detail.salePrice": "Sale Price",
    "detail.code": "Code",
    "detail.condo": "Condo fee",
    "detail.month": "month",
    "detail.year": "year",
    "detail.whatsappChat": "Chat on WhatsApp",
    "detail.moreInfo": "I want to know this property",
    "detail.gallery": "Photo Gallery",
    "detail.video": "Property Video",
    "detail.about": "About the Property",
    "detail.highlights": "Property Highlights",
    "detail.highlightsSub": "Features that make this property unique in {neighborhood}",
    "detail.fullDetails": "Full Details",
    "detail.location": "Prime Location",
    "detail.brokerTitle": "Responsible Broker",
    "detail.scheduleTitle": "Schedule Your Visit Now",
    "detail.scheduleSub": "Fill in your details and we'll get in touch within minutes. No commitment!",
    "detail.fullName": "Full name*",
    "detail.namePlaceholder": "Your name",
    "detail.phonePlaceholder": "(21) 99999-9999",
    "detail.phoneLabel": "Phone / WhatsApp*",
    "detail.emailLabel": "Email",
    "detail.messageLabel": "Message",
    "detail.messagePlaceholder": "I'd like to schedule a visit...",
    "detail.submitSchedule": "I Want to Schedule My Visit",
    "detail.dataPrivacy": "Your data is safe. We don't share it with third parties.",
    "detail.messageSent": "Message Sent!",
    "detail.contactSoon": "We'll get in touch soon.",
    "detail.orWhatsApp": "Or chat on WhatsApp now",
    "detail.notFound": "Didn't find your ideal {type}?",
    "detail.notFoundSub": "Tell us what you're looking for and our team will find the best options for you.",
    "detail.requestReceived": "We received your request!",
    "detail.personalizedOptions": "We'll get in touch with personalized options.",
    "detail.helpFind": "Help me find my {type}",
    "detail.yourName": "Your name *",
    "detail.whatsappRequired": "WhatsApp *",
    "detail.emailOptional": "Email (optional)",
    "detail.similarIn": "Similar {type}s in {neighborhood}",
    "detail.seeOther": "See other options that might interest you",
    "detail.whatsappMsg": "Hi! I'm interested in the property: {title} - {price}. I'd like to schedule a visit.",
    "detail.leadMsg": "Hi! My name is {name}.\nPhone: {phone}\nEmail: {email}\nMessage: {message}\n\nI'm interested in: {title} - {price}",
    "detail.leadNotFound": "Hi! My name is {name}.\nPhone: {phone}\nEmail: {email}\n\nI couldn't find my ideal {type}, can you help?",
    "detail.notProvided": "not provided",

    // Launches
    "nav.launches": "New Developments",
    "launches.title": "New Developments",
    "launches.subtitle": "Discover the finest new real estate developments in Rio de Janeiro. Exclusive launch conditions.",
    "launches.empty": "No launches available at the moment. Check back soon!",
    "launches.from": "From",
    "launches.upTo": "Up to",
    "launches.area": "Area",
    "launches.downPayment": "Down Payment",
    "launches.delivery": "Delivery",
    "launches.typologies": "Typologies",
    "launches.description": "Description",
    "launches.profitability": "Profitability Study",
    "launches.ctaTitle": "Receive Floor Plans & Project Book",
    "launches.ctaDesc": "Talk to a consultant and receive all the project material via WhatsApp.",
    "launches.ctaButton": "Receive Material via WhatsApp",
    "launches.backToList": "Back to Developments",
    "launches.notFound": "Development not found.",
    "launches.form.title": "Financing Simulation",
    "launches.form.subtitle": "Fill in your details and receive a personalized simulation.",
    "launches.form.name": "Full name",
    "launches.form.phone": "Phone number",
    "launches.form.email": "Email (optional)",
    "launches.form.income": "Monthly family income (R$)",
    "launches.form.downPayment": "Down payment amount (R$)",
    "launches.form.submit": "Request Simulation",
    "launches.form.sending": "Sending...",
    "launches.form.success": "Request submitted!",
    "launches.form.successDesc": "We'll get in touch soon with your simulation.",
    "launches.form.error": "Error submitting. Please try again.",
    "launches.carousel.title": "Featured Developments",
    "launches.carousel.subtitle": "The best new developments with exclusive conditions",
    "launches.carousel.viewAll": "View All Developments",
    "listings.suites": "suites",

    // Footer
    "footer.tagline": "Specialists in premium real estate in Barra da Tijuca, South Zone and Tijuca.",
    "footer.regions": "Regions",
    "footer.contact": "Contact",
    "footer.rights": "Topclass Premium Real Estate. All rights reserved. CRECI-RJ",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>("pt");

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};